import { useEffect, useRef } from 'react'
import { useVaultStore } from '@/stores/vaultStore'
import { useSettingsStore } from '@/stores/settingsStore'

/**
 * Hook global : lock tous les coffres si inactivité > autoLockMs,
 * et à la perte de focus/visibilité si lockOnBlur=true.
 */
export function useAutoLock() {
  const lockAll = useVaultStore((s) => s.lockAll)
  const hasUnlockedVaults = useVaultStore((s) => Object.keys(s.unlocked).length > 0)
  const autoLockMs = useSettingsStore((s) => s.autoLockMs)
  const lockOnBlur = useSettingsStore((s) => s.lockOnBlur)

  const lastActivityRef = useRef(0)

  useEffect(() => {
    const onActivity = () => { lastActivityRef.current = Date.now() }
    onActivity()
    const events = ['mousemove', 'keydown', 'click', 'touchstart', 'scroll']
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }))
    return () => events.forEach((e) => window.removeEventListener(e, onActivity))
  }, [])

  useEffect(() => {
    if (!hasUnlockedVaults) {
      lastActivityRef.current = Date.now()
      return
    }
    if (lastActivityRef.current === 0) lastActivityRef.current = Date.now()

    const lockIfInactive = () => {
      if (Date.now() - lastActivityRef.current >= autoLockMs) {
        lockAll()
        lastActivityRef.current = Date.now()
      }
    }

    lockIfInactive()
    const interval = setInterval(() => {
      lockIfInactive()
    }, 5_000)
    return () => clearInterval(interval)
  }, [hasUnlockedVaults, autoLockMs, lockAll])

  useEffect(() => {
    if (!lockOnBlur) return

    const lockIfAnyVaultIsOpen = () => {
      if (Object.keys(useVaultStore.getState().unlocked).length > 0) lockAll()
    }
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') lockIfAnyVaultIsOpen()
    }

    window.addEventListener('blur', lockIfAnyVaultIsOpen)
    window.addEventListener('pagehide', lockIfAnyVaultIsOpen)
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      window.removeEventListener('blur', lockIfAnyVaultIsOpen)
      window.removeEventListener('pagehide', lockIfAnyVaultIsOpen)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [lockOnBlur, lockAll])
}
